import { useAuth } from "react-oidc-context";
import { useContext, useState } from "react";
import {
	Avatar,
	Button,
	Card,
	FileInput,
	Label,
	Table,
	Modal,
	Spinner,
} from "flowbite-react";
import { HiOutlineArrowRight, HiCamera } from "react-icons/hi";
import { PlayerTable } from "../pages/Player";
import { SportsMapInv, YearMap } from "../pages/SportMap";
import { StripeSetup } from "./Stripe";
import { GenContract, ListContracts } from "./Contract";
import { UIContext } from "../UIContext";

import { useQuery } from "@tanstack/react-query";

import { isHeic, heicTo } from "heic-to";

const fetchPlayerSelf = async (auth) => {
	const response = await fetch(`/api/player/self`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${auth.user.access_token}`,
		},
	});

	if (!response.ok) {
		throw new Error("Could Not Fetch Player Data");
	}

	return response.json();
};

const getPlayerSelf = (auth) => {
	return useQuery({
		queryKey: ["playerData"],
		queryFn: async () => fetchPlayerSelf(auth),
		refetchOnWindowFocus: false,
		enabled: true,
	});
};

export function AthleteMain() {
	const auth = useAuth();
	const ctx = useContext(UIContext);
	/*const [playerData, setPlayerData] = useState();*/
	const [openModal, setOpenModal] = useState(false);
	const USD = Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	});

	const { data: playerData, isLoading, isError } = getPlayerSelf(auth);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<Spinner size="xl" />
			</div>
		);
	}

	if (isError || !playerData) {
		return (
			<div className="flex justify-center items-center h-screen">
				<p className="text-red-500">Could not fetch Player Data</p>
			</div>
		);
	}
	var initials, i;
	i = playerData.name.trim().split(" ");
	if (i.length > 1) {
		initials = i[0].charAt(0) + i[i.length - 1].charAt(0);
	} else {
		initials = i[0].charAt(0);
	}
	let profile_pic = null;
	if ("profile_pic" in playerData.attrs === true) {
		profile_pic = playerData.attrs.profile_pic;
	}
	return (
		<div className="sm:w-[300px] md:w-[600px] mx-auto my-auto">
			<ModalComponent modalState={[openModal, setOpenModal]} />
			{/* ==========================================
					Player Table. Information about the player 
			    ==========================================  */}
			{playerData !== undefined ? (
				<div>
					{/* =======================================
						  Player Name
			        =======================================  */}
					<div className="text-xl text-center font-bold rounded-md w-full border-none bg-gray-100 py-4 my-4">
						{playerData.name}
					</div>
					<div className="flex flex-col sm:flex-row justify-center">
						{/* ============================
								Profile Picture Section 
								============================ */}
						<div className="flex flex-col px-24 justify-around">
							<div className="group w-fit h-fit relative justify-around rounded-md overflow-hidden">
								{profile_pic === null ? (
									<Avatar
										rounded
										size="xl"
										className="my-2"
										placeholderInitials={initials.toUpperCase()}
									/>
								) : (
									<img
										className="sm:max-w-64 sm:max-h-64 sm:object-cover"
										src={profile_pic}
									/>
								)}
								<Button
									color="gray"
									size="sm"
									onClick={() => setOpenModal(true)}
									className="absolute bottom-0 right-0
									transition transform translate-y-8 
									ease-in-out invisible absolute
								  group-hover:visible group-hover:translate-y-0"
								>
									<HiCamera />
								</Button>
							</div>
						</div>
						<div className="flex flex-col w-fit w-full justify-around border-l-2 border-gray-300">
							<div className="h-px my-2" />
							<div className="flex rounded-md w-full">
								<table className="border-separate mb:border-spacing-1 sm:border-spacing-x-4 sm:border-spacing-y-2 text-gray-500">
									<tbody>
										<tr className="py-2">
											<td>Sport</td>
											<td>:</td>
											<td className="px-6">{SportsMapInv[playerData.sport]}</td>
										</tr>

										<tr className="py-2">
											<td>Jersey </td>
											<td>:</td>
											<td className="px-6">{playerData.jersey}</td>
										</tr>

										<tr className="py-2">
											<td>Position</td>
											<td>:</td>
											<td className="px-6">{playerData.position}</td>
										</tr>

										<tr className="py-2">
											<td>Year</td>
											<td>:</td>
											<td className="px-6">{YearMap[playerData.year]}</td>
										</tr>

										<tr className="py-2">
											<td>Team</td>
											<td>:</td>
											<td className="px-6">{playerData.team.name}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
					{/* 
						===============================================
						Player Contract / Balance / Payment Information
						===============================================
					*/}
					<div className="h-px my-2" />
					{auth.user.profile.athlete_verified !== undefined ? (
						<div className="my-2 sm:my-4 py-auto">
							{auth.user.profile.athlete_verified === true ? (
								<>
									{/* This section is to show how much money this player has as a Balance*/}
									<div className="flex flex-col justify-around mx-auto my-12 sm:my-auto px-auto py-auto">
										<div className="flex-col mx-auto my-auto min-w-full text-lg justify-center">
											{/* 
											==============================================================
											Stripe Component. Used for setting up, and logging into Stripe
											==============================================================
										*/}
											<Card className="w-full">
												<StripeSetup auth={auth} playerData={playerData} />
											</Card>

											{/* 
												===========================================================
												Player Balance Information. Used to generate a new contract
												===========================================================
											*/}

											<Card className="w-full">
												<p>Current Endorsement Offer</p>
												<p>{USD.format(playerData.balance / 100)}</p>
												{playerData.balance > 99 ? (
													<div>
														<GenContract auth={auth} playerData={playerData} />
													</div>
												) : (
													<></>
												)}
											</Card>
										</div>
									</div>
									{/* 
										========================================================
										Player Contract Information. Contains list of generated,
										signed, and completed contracts
										========================================================
										*/}
									<div>
										<ListContracts auth={auth} playerData={playerData} />
									</div>
								</>
							) : (
								<div>
									{/* 
											===============================================
											Component to show if the Athlete isn't verified
											===============================================
										*/}
									<div>
										<p>
											Athlete Verification Status: <b>Unverified</b>
										</p>
									</div>
									<hr />
									<div>
										To verify your identity please send your verification
										documents to{" "}
										<a
											className="text-blue-600 underline font-bold"
											href="mailto:verify@nilo.win"
										>
											verify@nilo.win
										</a>
									</div>
								</div>
							)}
						</div>
					) : (
						<div>Athlete verification status: Unknown</div>
					)}
				</div>
			) : (
				<></>
			)}
		</div>
	);
}

const ModalComponent = (props) => {
	const [openModal, setOpenModal] = props.modalState;
	const ctx = useContext(UIContext);
	const auth = useAuth();
	const [file, setFile] = useState(null);
	const PreviewPic = () => {
		if (file !== null) {
			let imgsrc = URL.createObjectURL(file);
			return (
				<div className="w-fit h-fit justify-around rounded-md overflow-hidden">
					<img
						id="previewpic"
						className="sm:max-w-64 sm:max-h-64 sm:object-cover"
						src={imgsrc}
					></img>
				</div>
			);
		}
		return (
			<div>
				<Avatar size="lg" rounded className="my-2" />
			</div>
		);
	};

	const uploadFile = (e) => {
		e.preventDefault();

		if (!file) {
			ctx.setAlert({
				open: true,
				type: "Error",
				title: "File Error",
				message: "No File Selected",
			});
			return;
		}

		const allowedFileTypes = ["image/png", "image/jpeg", "image/gif"];
		if (allowedFileTypes.includes(file.type) == false) {
			ctx.setAlert({
				open: true,
				type: "Error",
				title: "File Error",
				message: "Invalid File Type. Allowed File Types: png, jpeg, gif",
			});
			return;
		}

		if (file.size > 10485760) {
			ctx.setAlert({
				open: true,
				type: "Error",
				title: "File Error",
				message: "Maximum Supported File Size: 10 MB",
			});
			return;
		}

		/*============================*/
		/* Get Presigned Upload URL from Server for AWS S3 Upload*/
		/*============================*/
		/*		fetch(`/api/presigned-url`, {
			method: "POST",
			body: JSON.stringify({ filetype: file.type }),
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${auth.user.access_token}`,
			},
		})
			.then((resp) => {
				if (!resp.ok) {
					throw new Error("Could not generate Upload URL");
				}
				return resp.json();
			})
			.then((resp) => {
				if (resp.url === undefined || resp.url === null) {
					ctx.setAlert({
						type: "Error",
						title: "Upload Error",
						message: `Could not upload file.
				Please contact NILO support at support@nilo.win`,
						open: true,
					});
					throw new Error("Could not generate Upload URL");
				}
				*/
		/*								Object.keys(resp.fields).forEach((key) => {
					data.append(key, resp.fields[key]);
				});
				*/
		//console.log("Uploading file to ", resp.url);

		/*============================*/
		/* Upload URL to server 			*/
		/*============================*/
		var data = new FormData();
		data.append("file", file);
		ctx.setLoading(true);
		fetch(`/api/upload/profile/pic`, {
			method: "POST",
			body: data,
			headers: {
				Authorization: `Bearer ${auth.user.access_token}`,
			},
		})
			.then((resp) => {
				ctx.setLoading(false);
				if (!resp.ok) {
					throw new Error("File Upload Failed");
				} else {
					setOpenModal(false);
					ctx.setAlert({
						open: true,
						type: "Success",
						title: "File Upload Status",
						message: "File Uploaded Successfully",
					});
				}
			})
			.catch((error) => {
				console.log(error);
				ctx.setAlert({
					type: "Error",
					title: "Upload Error",
					message: `Could not upload file. Please contact NILO support at support@nilo.win`,
					open: true,
				});
			});

		return;
	};

	const handleFileChange = (e) => {
		e.preventDefault();
		setFile(e.target.files[0]);
	};

	return (
		<Modal dismissable show={openModal} onClose={() => setOpenModal(false)}>
			<Modal.Header>Upload Profile Picture</Modal.Header>
			<Modal.Body>
				<PreviewPic />
				<form id="profilepic">
					<div className="flex space-y-6">
						<Label htmlFor="profilepic">Profile Picture</Label>
						<FileInput id="profilepic" onChange={handleFileChange} />
					</div>
					<hr className="space-y-6" />
					<div className="flex py-4 space-x-10">
						<Button color="gray" onClick={uploadFile}>
							Upload
						</Button>
					</div>
				</form>
			</Modal.Body>
		</Modal>
	);
};
